import type { RatingScaleOption, SkillLevelOption } from 'screens/ListGame/types';

export enum SkillLevelMode {
  Specified = 'SPECIFIED',
  AllLevels = 'ALL_LEVELS',
}

export const SKILL_LEVEL_MODE_OPTIONS: { label: string; value: SkillLevelMode }[] = [
  { label: 'Specified', value: SkillLevelMode.Specified },
  { label: 'All levels', value: SkillLevelMode.AllLevels },
];

/** The "Rating system" dropdown value that means a skill-level range instead of a scale. */
export const SELF_RATE_RATING_SYSTEM = 'SELF_RATE';

/** The form fields the field owns; host forms include these in their values type. */
export interface SkillLevelRangeFormValues {
  skillLevelMode: SkillLevelMode;
  /** A scale id for the sport, or SELF_RATE_RATING_SYSTEM. Only read when Specified. */
  ratingSystem: string | null;
  /** The chosen scale's range. */
  ratingMinimum: number | null;
  ratingMaximum: number | null;
  /** Self-rate level range (skill_levels ids); max null = a single level. */
  selfRatedSkillLevel: string | null;
  selfRatedMaxSkillLevel: string | null;
}

export const DEFAULT_SKILL_LEVEL_RANGE_VALUES: SkillLevelRangeFormValues = {
  skillLevelMode: SkillLevelMode.Specified,
  ratingSystem: null,
  ratingMinimum: null,
  ratingMaximum: null,
  selfRatedSkillLevel: null,
  selfRatedMaxSkillLevel: null,
};

export interface SkillLevelRangeFieldProps {
  /** The selected sport's rating systems (from the scale tables). */
  scales: RatingScaleOption[];
  /** Self-rate skill levels, rank ascending. */
  skillLevels: SkillLevelOption[];
}

export const isSpecifiedScale = (values: SkillLevelRangeFormValues) =>
  values.skillLevelMode === SkillLevelMode.Specified &&
  !!values.ratingSystem &&
  values.ratingSystem !== SELF_RATE_RATING_SYSTEM;

export const isSelfRated = (values: SkillLevelRangeFormValues) =>
  values.skillLevelMode === SkillLevelMode.Specified &&
  values.ratingSystem === SELF_RATE_RATING_SYSTEM;

/** The self-rate level range columns; both null when the player isn't self-rating. */
export const selfRatedLevelColumns = (values: SkillLevelRangeFormValues) => {
  if (!isSelfRated(values)) {
    return { selfRatedMinSkillLevelId: null, selfRatedMaxSkillLevelId: null };
  }
  return {
    selfRatedMinSkillLevelId: values.selfRatedSkillLevel,
    // A single picked level is a range of one.
    selfRatedMaxSkillLevelId: values.selfRatedMaxSkillLevel ?? values.selfRatedSkillLevel,
  };
};

/** "DUPR 3 - 5" / "Intermediate - Advanced" — the listing title's rating half. */
export const buildSkillSummary = (
  values: SkillLevelRangeFormValues,
  scales: RatingScaleOption[],
  skillLevels: SkillLevelOption[],
): string | undefined => {
  if (values.skillLevelMode !== SkillLevelMode.Specified || !values.ratingSystem) return;
  if (values.ratingSystem === SELF_RATE_RATING_SYSTEM) {
    const nameOf = (id: string | null) => skillLevels.find((level) => level.id === id)?.displayName;
    const min = nameOf(values.selfRatedSkillLevel);
    const max = nameOf(values.selfRatedMaxSkillLevel);
    return min && max && max !== min ? `${min} - ${max}` : min;
  }
  const scale = scales.find((option) => option.id === values.ratingSystem);
  if (!scale) return;
  return values.ratingMinimum != null && values.ratingMaximum != null
    ? `${scale.shortName} ${values.ratingMinimum} - ${values.ratingMaximum}`
    : scale.shortName;
};
